import React from "react"
import { useState, useEffect } from "react"
import { Card, Container, Row, Col, Button, Badge, Spinner, Modal, Form, Alert } from "react-bootstrap"
import { Link } from "react-router-dom"
import {
  FaUsers,
  FaUser,
  FaCalendarAlt,
  FaUserPlus,
  FaCalendarPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaStethoscope,
  FaChartLine,
  FaArrowRight,
  FaShieldAlt,
  FaCar,
} from "react-icons/fa";
import { FiActivity } from "react-icons/fi";
import { IoIosTrendingUp } from "react-icons/io";
import { useAuth } from "../../context/AuthContext"
import apiService from "../../services/api"

// CSS styles for hover effects
const hoverLiftStyles = `
  .hover-lift {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  }
  .hover-lift:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }
`;

export default function AdminDashboard() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({})
  const [recentPatients, setRecentPatients] = useState([])
  const [todayAppointments, setTodayAppointments] = useState([])
  const [recentDrivers, setRecentDrivers] = useState([])
  
  // Appointment scheduling modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [appointmentForm, setAppointmentForm] = useState({
    patientId: '',
    doctorName: '',
    appointmentDate: '',
    appointmentTime: '',
    notes: '',
    price: ''
  })
  const [patients, setPatients] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  // Patient modal state
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [patientForm, setPatientForm] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    password: ''
  })
  const [patientSubmitting, setPatientSubmitting] = useState(false)

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch admin dashboard data
      const dashboardResponse = await apiService.getAdminDashboard()
      if (dashboardResponse.success) {
        setStats(dashboardResponse.data.stats)
        setRecentPatients(dashboardResponse.data.recentPatients)
        setTodayAppointments(dashboardResponse.data.recentAppointments)
      }

      // Fetch recent drivers
      try {
        const driversResponse = await apiService.getDrivers(1, 5)
        if (driversResponse.success) {
          setRecentDrivers(driversResponse.data)
        }
      } catch (error) {
        console.error('Error fetching recent drivers:', error)
      }

      // Fetch patients and doctors for appointment scheduling
      try {
        // Only fetch patients, not doctors
        const patientsResponse = await apiService.getPatients(1, 100);
        
        if (patientsResponse.success) {
          // Handle different response structures
          if (patientsResponse.data && patientsResponse.data.patients) {
            setPatients(patientsResponse.data.patients);
          } else if (patientsResponse.data && Array.isArray(patientsResponse.data)) {
            setPatients(patientsResponse.data);
          } else {
            console.log('Patients response structure:', patientsResponse);
            setPatients([]);
          }
        } else {
          console.error('Failed to fetch patients:', patientsResponse);
          setPatients([]);
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
        setPatients([]);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const openAppointmentModal = () => {
    setShowAppointmentModal(true)
    // Set default date to today
    const today = new Date().toISOString().split('T')[0]
    setAppointmentForm(prev => ({ ...prev, appointmentDate: today }))
  }

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    
    try {
      const appointmentData = {
        ...appointmentForm,
        adminId: user.id // Include admin ID for appointment ownership
      }
      
      const response = await apiService.createAppointment(appointmentData)
      if (response.success) {
        setMessage({ type: 'success', text: 'Appointment scheduled successfully!' })
        setShowAppointmentModal(false)
        setAppointmentForm({
          patientId: '',
          doctorName: '',
          appointmentDate: '',
          appointmentTime: '',
          notes: '',
          price: ''
        })
        // Refresh dashboard data instead of page reload
        await fetchDashboardData()
      }
    } catch (error) {
      console.error('Error creating appointment:', error)
      setMessage({ type: 'danger', text: error.message || 'Failed to schedule appointment' })
    } finally {
      setSubmitting(false)
    }
  }

  const openPatientModal = () => {
    setShowPatientModal(true)
    setPatientForm({
      name: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      password: ''
    })
  }

  const handlePatientSubmit = async (e) => {
    e.preventDefault()
    setPatientSubmitting(true)
    
    try {
      const patientData = {
        ...patientForm,
        password: patientForm.password || undefined // Only send password if provided
      }
      
      const response = await apiService.createPatient(patientData)
      if (response.success) {
        setMessage({ type: 'success', text: 'Patient created successfully!' })
        setShowPatientModal(false)
        setPatientForm({
          name: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          gender: '',
          password: ''
        })
        // Refresh dashboard data to show new patient
        await fetchDashboardData()
      }
    } catch (error) {
      console.error('Error creating patient:', error)
      setMessage({ type: 'danger', text: error.message || 'Failed to create patient' })
    } finally {
      setPatientSubmitting(false)
    }
  }

  const getStatusVariant = (status) => {
    switch (status) {
      case "Active":
        return "success"
      case "Inactive":
        return "secondary"
      case "Scheduled":
        return "primary"
      case "In Progress":
        return "warning"
      case "Completed":
        return "success"
      default:
        return "secondary"
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <FaCheckCircle />
      case "In Progress":
        return <FaClock />
      case "Scheduled":
        return <FaCalendarAlt />
      default:
        return <FaExclamationTriangle />
    }
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#f8fffe" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <style>{hoverLiftStyles}</style>
      
      <Container className="py-5">
        {/* Hero Section */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
            }}
          >
            {/* Background Pattern */}
            <div className="position-absolute w-100 h-100 top-0 start-0">
              <div
                className="position-absolute rounded-circle opacity-10"
                style={{
                  width: "200px",
                  height: "200px",
                  background: "white",
                  top: "-50px",
                  right: "-50px",
                }}
              ></div>
            </div>

            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaShieldAlt className="me-2" />
                    Admin Dashboard
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">Aidify Management</h1>
                <p className="fs-5 opacity-90 mb-4">Monitor and manage your Aidify facility operations</p>
                <div className="d-flex gap-3">
                  <Button
                    variant="light"
                    className="px-4 py-2 rounded-pill fw-medium"
                    style={{
                      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                    }}
                    onClick={openPatientModal}
                  >
                    <FaUserPlus className="me-2" style={{ color: "#0891b2" }} />
                    <span style={{ color: "#0891b2" }}>Add Patient</span>
                  </Button>
                  <Button
                    variant="outline-light"
                    className="px-4 py-2 rounded-pill fw-medium"
                    style={{
                      borderWidth: "2px",
                      backdropFilter: "blur(10px)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    }}
                    onClick={openAppointmentModal}
                  >
                    <FaCalendarPlus className="me-2" />
                    Schedule Appointment
                  </Button>
                </div>
              </Col>
              <Col md={4} className="text-center">
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style={{
                    width: "120px",
                    height: "120px",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FaShieldAlt size={48} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Navigation Links */}
        <Row className="g-4 mb-4">
          <Col lg={4} md={6}>
            <Link to="/admin/patients" className="text-decoration-none">
              <Card className="border-0 rounded-4 h-100 text-center p-4 hover-lift" style={{ backgroundColor: "#f0fdfa" }}>
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                  }}
                >
                  <FaUsers className="text-white" size={24} />
                </div>
                <h5 className="fw-bold mb-2">Manage Patients</h5>
                <p className="text-muted mb-0">View and manage patient records</p>
              </Card>
            </Link>
          </Col>
          <Col lg={4} md={6}>
            <Link to="/admin/appointments" className="text-decoration-none">
              <Card className="border-0 rounded-4 h-100 text-center p-4 hover-lift" style={{ backgroundColor: "#f0fdfa" }}>
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  }}
                >
                  <FaCalendarAlt className="text-white" size={24} />
                </div>
                <h5 className="fw-bold mb-2">Appointments</h5>
                <p className="text-muted mb-0">Schedule and manage appointments</p>
              </Card>
            </Link>
          </Col>
          <Col lg={4} md={6}>
            <Link to="/admin/drivers" className="text-decoration-none">
              <Card className="border-0 rounded-4 h-100 text-center p-4 hover-lift" style={{ backgroundColor: "#f0fdfa" }}>
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                  }}
                >
                  <FaCar className="text-white" size={24} />
                </div>
                <h5 className="fw-bold mb-2">Manage Drivers</h5>
                <p className="text-muted mb-0">Approve and manage drivers</p>
              </Card>
            </Link>
          </Col>
          {/* <Col lg={3} md={6}>
            <Link to="/admin/doctors" className="text-decoration-none">
              <Card className="border-0 rounded-4 h-100 text-center p-4 hover-lift" style={{ backgroundColor: "#f0fdfa" }}>
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  }}
                >
                  <FaStethoscope className="text-white" size={24} />
                </div>
                <h5 className="fw-bold mb-2">Manage Doctors</h5>
                <p className="text-muted mb-0">View and manage doctor profiles</p>
              </Card>
            </Link>
          </Col> */}
        </Row>

        {/* Statistics Cards */}
        <Row className="g-4 mb-5">
          <Col lg={6} md={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    }}
                  >
                    <FaUsers className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1">{stats.totalPatients || 0}</h3>
                    <p className="text-muted mb-0">Total Patients</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          {/* <Col lg={3} md={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                    }}
                  >
                    <FaCalendarAlt className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1">{stats.todayAppointments || 0}</h3>
                    <p className="text-muted mb-0">Today's Appointments</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                    }}
                  >
                    <FaExclamationTriangle className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1">{stats.pendingAppointments || 0}</h3>
                    <p className="text-muted mb-0">Pending Appointments</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col> */}
          <Col lg={6} md={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                    }}
                  >
                    <FaUserPlus className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1">{stats.newPatientsThisMonth || 0}</h3>
                    <p className="text-muted mb-0">New Patients This Month</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          {/* Recent Patients */}
          <Col lg={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">Recent Patients</h5>
                  <Link to="/admin/patients">
                    <Button variant="link" className="text-decoration-none p-0">
                      View All <FaArrowRight className="ms-1" />
                    </Button>
                  </Link>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {recentPatients.length > 0 ? (
                  recentPatients.map((patient) => (
                    <div
                      key={patient.id}
                      className="d-flex align-items-center p-3 rounded-3 mb-3"
                      style={{ backgroundColor: "white" }}
                    >
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                        }}
                      >
                        <FaUser className="text-white" size={20} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{patient.name}</h6>
                        <p className="text-muted mb-1">{patient.email}</p>
                        <small className="text-muted">
                          {new Date(patient.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      <div className="text-end">
                        <Badge bg="success" className="rounded-pill">
                          {patient.total_appointments} appointments
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <FaUsers size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No recent patients</h6>
                    <p className="text-muted">New patient registrations will appear here</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Today's Appointments */}
          {/* <Col lg={4}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">Today's Appointments</h5>
                  <Link to="/admin/appointments">
                    <Button variant="link" className="text-decoration-none p-0">
                      View All <FaArrowRight className="ms-1" />
                    </Button>
                  </Link>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {todayAppointments.length > 0 ? (
                  todayAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="d-flex align-items-center p-3 rounded-3 mb-3"
                      style={{ backgroundColor: "white" }}
                    >
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                        }}
                      >
                        <FaCalendarAlt className="text-white" size={20} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{appointment.patient_name}</h6>
                        <p className="text-muted mb-1">
                          {appointment.appointment_time} - {appointment.doctor_name}
                        </p>
                        <small className="text-muted">{appointment.doctor_specialty}</small>
                      </div>
                      <div className="text-end">
                        <Badge bg={getStatusVariant(appointment.status)} className="rounded-pill">
                          {appointment.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <FaCalendarAlt size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No appointments today</h6>
                    <p className="text-muted">All clear for today!</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col> */}

          {/* Recent Drivers */}
          <Col lg={6}>
            <Card className="border-0 rounded-4 h-100" style={{ backgroundColor: "#f0fdfa" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">Recent Drivers</h5>
                  <Link to="/admin/drivers">
                    <Button variant="link" className="text-decoration-none p-0">
                      View All <FaArrowRight className="ms-1" />
                    </Button>
                  </Link>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {recentDrivers.length > 0 ? (
                  recentDrivers.map((driver) => (
                    <div
                      key={driver.id}
                      className="d-flex align-items-center p-3 rounded-3 mb-3"
                      style={{ backgroundColor: "white" }}
                    >
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                        }}
                      >
                        <FaCar className="text-white" size={20} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">{driver.name}</h6>
                        <p className="text-muted mb-1">{driver.car_name}</p>
                        <small className="text-muted">
                          {new Date(driver.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      {/* <div className="text-end">
                        <Badge bg={getStatusVariant(driver.status)} className="rounded-pill">
                          {driver.status}
                        </Badge>
                      </div> */}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <FaCar size={48} className="text-muted mb-3" />
                    <h6 className="text-muted">No recent drivers</h6>
                    <p className="text-muted">New driver registrations will appear here</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Appointment Scheduling Modal */}
      <Modal show={showAppointmentModal} onHide={() => setShowAppointmentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule New Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} className="mb-3">
              {message.text}
            </Alert>
          )}
          <Form onSubmit={handleAppointmentSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Patient</Form.Label>
              <Form.Select
                value={appointmentForm.patientId}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, patientId: e.target.value }))}
                required
              >
                <option value="">Select a Patient</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Doctor</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter doctor name"
                value={appointmentForm.doctorName}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, doctorName: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Appointment Date</Form.Label>
              <Form.Control
                type="date"
                value={appointmentForm.appointmentDate}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, appointmentDate: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Appointment Time</Form.Label>
              <Form.Control
                type="time"
                value={appointmentForm.appointmentTime}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, appointmentTime: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price (Optional)</Form.Label>
              <Form.Control
                type="number"
                value={appointmentForm.price}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, price: e.target.value }))}
              />
            </Form.Group>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Submitting...
                </>
              ) : (
                "Schedule Appointment"
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Patient Creation Modal */}
      <Modal show={showPatientModal} onHide={() => setShowPatientModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} className="mb-3">
              {message.text}
            </Alert>
          )}
          <Form onSubmit={handlePatientSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter patient's full name"
                value={patientForm.name}
                onChange={(e) => setPatientForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter patient's email"
                value={patientForm.email}
                onChange={(e) => setPatientForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter patient's phone number"
                value={patientForm.phone}
                onChange={(e) => setPatientForm(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth *</Form.Label>
              <Form.Control
                type="date"
                value={patientForm.dateOfBirth}
                onChange={(e) => setPatientForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Gender *</Form.Label>
              <Form.Select
                value={patientForm.gender}
                onChange={(e) => setPatientForm(prev => ({ ...prev, gender: e.target.value }))}
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password (Optional)</Form.Label>
              <Form.Control
                type="password"
                placeholder="Leave blank for default password (patient123)"
                value={patientForm.password}
                onChange={(e) => setPatientForm(prev => ({ ...prev, password: e.target.value }))}
              />
              <Form.Text className="text-muted">
                If no password is provided, a default password will be set.
              </Form.Text>
            </Form.Group>
            <Button variant="primary" type="submit" disabled={patientSubmitting}>
              {patientSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Creating...
                </>
              ) : (
                "Create Patient"
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  )
}
